import React, { useState, useEffect, ChangeEvent, useMemo } from 'react';

import Title from '@vromo/ui/components/Title';
import Button from '@vromo/ui/components/Button';
import Select from '@vromo/ui/components/Select';
import Input from '@vromo/ui/components/Input';
import { ModalHeader, ModalContent, ModalActions } from '@vromo/ui/components/Modal';

import { update } from 'serializr';

import { action, when } from 'mobx';
import { useStore } from '@vromo/mobx-react';
import { observer } from 'mobx-react-lite';
import { RootStore } from '../../stores/RootStore';
import { useToasts } from '@vromo/ui/components/Toasts';
import { Group } from '@vromo/models';
import newEntityListFrom from '@vromo/models/helpers';
import { qaLabelTeams } from './../../../../../common-qa-labels/teams';

export default observer(function NewTeamForm({ close }: { close: () => void }) {
    const {
        groupStore,
        authStore: { graph, role }
    } = useStore<RootStore>('rootStore');

    const [name, setName] = useState('');
    const [parentTeam, setParentTeam] = useState(0);

    const showToast = useToasts();

    useEffect(() => {
        return when(
            () => groupStore.loaded,
            () => setParentTeam(groupStore.groups?.first()?.id || 0)
        );
    }, []);

    const subgroupsInSelectedTeams = useMemo(
        () =>
            groupStore.groups
                .filter((group) => group.parent === parentTeam)
                .map(({ name }) => name?.trim()),
        [groupStore, parentTeam]
    );

    const isValidGroup = () => {
        const allFieldsFilled = !!name.trim();
        const groupNameNotUsed = !subgroupsInSelectedTeams.includes(name.trim());
        const showError = (validated: boolean, message: string) => {
            if (validated) {
                return true;
            } else {
                showToast(message);
                return false;
            }
        };

        return (
            showError(allFieldsFilled, 'Please fill out all fields before adding a new team.') &&
            showError(groupNameNotUsed, 'This group name is already in use.')
        );
    };

    function saveGroup() {
        if (!isValidGroup()) return;

        const newGroup = newEntityListFrom<Group>(Group, [
            { name: name, parent: parentTeam, parents: { items: [{ id: parentTeam }] } }
        ]);

        graph.root
            .graph()
            .roles(role.id)
            .groups()
            .post(newGroup)
            .then(
                action(({ items }) => {
                    setName('');
                    close();
                    update(groupStore.groups, { items, type: 'create' });
                    showToast(`Successfully added group ${name}`, 'success');
                })
            )
            .catch((e) => {
                console.error(e);
                showToast('Something went wrong. Please try again.', 'error');
            });
    }

    return (
        <>
            <ModalHeader>
                <Title data-testid={qaLabelTeams.newTeamModal.title}>Create a New Team</Title>
            </ModalHeader>
            <ModalContent>
                <Input
                    data-testid={qaLabelTeams.newTeamModal.nameInput}
                    label="Name"
                    style={{ marginBottom: 10 }}
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                />
                <label>Parent Group</label>
                <Select
                    data-testid={qaLabelTeams.newTeamModal.parentGroupDropdown}
                    style={{ marginBottom: 10 }}
                    onChange={(e: ChangeEvent<HTMLSelectElement>) =>
                        setParentTeam(parseInt(e.target.value))
                    }>
                    {groupStore.groups.map((it) => (
                        <option key={it.id} value={it.id!}>
                            {it.name}
                        </option>
                    ))}
                </Select>
            </ModalContent>
            <ModalActions>
                <Button onClick={close}>Cancel</Button>
                <Button data-testid={qaLabelTeams.newTeamModal.saveTeam} onClick={saveGroup} primary>
                    Save
                </Button>
            </ModalActions>
        </>
    );
});
